import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AppBar, Toolbar, Box, Button, CssBaseline, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { SnackbarProvider } from './context/SnackbarContext';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { initGA, trackPageView } from './analytics';
import ProductList from './components/ProductList';
import NotFound from './components/NotFound';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy load components
const ProductDetail = lazy(() => import('./components/ProductDetail'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const HowItWorks = lazy(() => import('./components/HowItWorks'));
const PWAGuide = lazy(() => import('./components/PWAGuide'));
const Admin = lazy(() => import('./components/Admin'));

// Komponent för att spåra sidändringar
function Analytics() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });

  // Dropdown menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Skapa theme baserat på dark mode
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#90caf9' : '#1976d2',
      },
      secondary: {
        main: darkMode ? '#f48fb1' : '#dc004e',
      },
      background: {
        default: darkMode ? '#121212' : '#ffffff',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
    },
  });

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  // Initiera Google Analytics när appen startar
  useEffect(() => {
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (measurementId && measurementId !== 'G-XXXXXXXXXX') {
      initGA(measurementId);
    }

    // Lyssna på navigationsmeddelanden från service worker
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'NAVIGATE' && event.data.url) {
        window.location.href = event.data.url;
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <title>Hitta bästa deals och erbjudanden - hittadeals.com</title>
        <meta name="description" content="Hitta de bästa deals och erbjudandena online. Jämför priser, upptäck rabatter och spara pengar på alla dina köp. Uppdateras dagligen med nya fynd!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hittadeals.com/" />
        <meta property="og:title" content="Hitta bästa deals och erbjudanden - hittadeals.com" />
        <meta property="og:description" content="Hitta de bästa deals och erbjudandena online. Jämför priser, upptäck rabatter och spara pengar på alla dina köp. Uppdateras dagligen med nya fynd!" />
        <meta property="og:image" content="/images/hittadeals-social-share.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="HittaDeals" />
        <meta property="og:locale" content="sv_SE" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://hittadeals.com/" />
        <meta property="twitter:title" content="Hitta bästa deals och erbjudanden - hittadeals.com" />
        <meta property="twitter:description" content="Hitta de bästa deals och erbjudandena online. Jämför priser, upptäck rabatter och spara pengar på alla dina köp. Uppdateras dagligen med nya fynd!" />
        <meta property="twitter:image" content="/images/hittadeals-social-share.jpg" />
      </Helmet>
      <ThemeProvider theme={theme}>
        <SnackbarProvider>
          <CssBaseline />
        <Router>
        <Analytics />
        
        <AppBar position="sticky" sx={{ 
          zIndex: 1201, 
          background: darkMode 
            ? 'linear-gradient(90deg, #424242 0%, #303030 100%)' 
            : 'linear-gradient(90deg, #1976d2 0%, #1565c0 100%)', 
          boxShadow: 4,
          padding: { xs: '15px 0', sm: '20px 0' },
          minHeight: '60px'
        }}>
          <Toolbar sx={{ minHeight: { xs: '60px', sm: '60px' } }}>
            <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
              <Link to="/">
                <img 
                  src="/images/hittadeals-logo.png" 
                  alt="Hitta Deals Logo" 
                  style={{ 
                    height: { xs: '40px', sm: '50px' },
                    maxHeight: '60px',
                    width: 'auto'
                  }} 
                />
              </Link>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button 
                color="inherit" 
                onClick={handleMenuClick}
                sx={{ 
                  mr: 1,
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  minWidth: { xs: '60px', sm: 'auto' }
                }}
                aria-controls={open ? 'info-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                Info
              </Button>
            <Menu
              id="info-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'info-button',
              }}
            >
              <MenuItem 
                onClick={handleMenuClose} 
                component={Link} 
                to="/om-oss"
              >
                Om oss
              </MenuItem>
              <MenuItem 
                onClick={handleMenuClose} 
                component={Link} 
                to="/sa-funkar-det"
              >
                Så funkar det
              </MenuItem>
              <MenuItem 
                onClick={handleMenuClose} 
                component={Link} 
                to="/pwa-guide"
              >
                Installera som app
              </MenuItem>
            </Menu>
              <IconButton 
                color="inherit" 
                onClick={toggleDarkMode}
                sx={{ 
                  ml: { xs: 0.5, sm: 1 },
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  padding: { xs: '6px', sm: '8px' },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'scale(1.05)',
                  }
                }}
                title={darkMode ? 'Växla till ljust läge' : 'Växla till mörkt läge'}
              >
                {darkMode ? <Brightness7 sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} /> : <Brightness4 sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        {/* <Container style={{ marginTop: '2rem' }}> */}
        <Box sx={{ width: '100%', mt: 0 }}>
          <ErrorBoundary>
            <Suspense fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress size={60} thickness={4} />
              </Box>
            }>
              <Routes>
                <Route path="/" element={<ProductList />} />
                <Route path="/produkt/:id" element={<ProductDetail />} />
                <Route path="/om-oss" element={<AboutUs />} />
                <Route path="/sa-funkar-det" element={<HowItWorks />} />
                <Route path="/pwa-guide" element={<PWAGuide />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </Box>
          {/* </Container> */}

          </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}



export default App;
